/*
  Mood quiz and gift finder (sections/mm-mood-quiz.liquid) and the mood hint
  (snippets/mm-mood-hint.liquid).

  Three parts, in this order:
    1. Pure logic (scoring, exclusions, picking, budgets, price text). No DOM, no network,
       exported for node so it can be tested: docs/build-2026-09/E-mood-quiz.test.mjs
    2. The quiz itself, one instance per [data-mm-quiz] element.
    3. The hint, every [data-mm-mood-hint] element on the page.

  Remembered mood (self mode only, a gift is somebody else's mood):
    localStorage.mmMood = { mood, second, at }
  Events on document:
    mm:mood          detail { mood, second, at, source: 'quiz' }   fired when a self result is reached
    mm:welcome-open  detail { source, mood }                       asks the welcome popup to open
  Analytics (Shopify.analytics.publish, picked up by the "MM wishlist" custom pixel and sent to Google Analytics;
  owner, 2 Oct 2026):
    mm_quiz_start     { mode: 'self' | 'gift' }                     the first answer of a run
    mm_quiz_complete  { mode, mood, second, budget, who }           a result reached by answering (not a reload)
*/
(function (root) {
  'use strict';

  /* ---------- 1. Pure logic ---------- */

  var MOODS = ['curator', 'social', 'sanctuary', 'ritual'];

  /* Budget bands in rupees. min is inclusive; max is inclusive unless maxExclusive is set. */
  var BUDGETS = {
    'under-1000': { min: 0, max: 1000, maxExclusive: true },
    '1000-2500': { min: 1000, max: 2500 },
    'above-2500': { min: 2500, max: Infinity, minExclusive: true },
    'none': { min: 0, max: Infinity }
  };

  function isMood(value) {
    return MOODS.indexOf(value) !== -1;
  }

  /*
    answers: the mood chosen at each question, in order.
    Top mood: highest score. A tie goes to the mood picked most recently, so the
    result is always one mood and never depends on the order of the MOODS list.
    Runner up: the next mood, only when it scored at least one point and sits
    within one point of the top.
  */
  function scoreAnswers(answers) {
    var scores = { curator: 0, social: 0, sanctuary: 0, ritual: 0 };
    var last = { curator: -1, social: -1, sanctuary: -1, ritual: -1 };
    (answers || []).forEach(function (mood, index) {
      if (!isMood(mood)) return;
      scores[mood] += 1;
      last[mood] = index;
    });
    var ranked = MOODS.slice().sort(function (a, b) {
      if (scores[b] !== scores[a]) return scores[b] - scores[a];
      if (last[b] !== last[a]) return last[b] - last[a];
      return MOODS.indexOf(a) - MOODS.indexOf(b);
    });
    var top = scores[ranked[0]] > 0 ? ranked[0] : null;
    var second = null;
    if (top && scores[ranked[1]] > 0 && scores[top] - scores[ranked[1]] <= 1) second = ranked[1];
    return { scores: scores, mood: top, second: second };
  }

  function hasTag(product, tag) {
    var want = String(tag).toLowerCase();
    var tags = product && product.tags;
    if (typeof tags === 'string') tags = tags.split(',');
    return (tags || []).some(function (t) { return String(t).trim().toLowerCase() === want; });
  }

  function firstAvailableVariant(product) {
    var variants = (product && product.variants) || [];
    for (var i = 0; i < variants.length; i++) if (variants[i].available) return variants[i];
    return null;
  }

  function isAvailable(product) {
    return !!firstAvailableVariant(product);
  }

  /* Price of the variant a shopper would get: the first available one, else the first. */
  function priceOf(product) {
    var variant = firstAvailableVariant(product) || ((product && product.variants) || [])[0];
    var value = variant ? parseFloat(variant.price) : NaN;
    return isNaN(value) ? null : value;
  }

  function compareAtOf(product) {
    var variant = firstAvailableVariant(product) || ((product && product.variants) || [])[0];
    var value = variant && variant.compare_at_price ? parseFloat(variant.compare_at_price) : NaN;
    var price = priceOf(product);
    return !isNaN(value) && price !== null && value > price ? value : null;
  }

  function isGiftCard(product) {
    var type = String((product && product.product_type) || '').toLowerCase();
    var handle = String((product && product.handle) || '').toLowerCase();
    return type === 'gift card' || type === 'gift cards' || handle.indexOf('gift-card') !== -1 || hasTag(product, 'gift card');
  }

  /* Never shown anywhere: internal and charm products, gift cards, anything without a price. */
  function isExcluded(product) {
    if (!product || !product.handle) return true;
    if (hasTag(product, 'internal') || hasTag(product, 'charm')) return true;
    if (isGiftCard(product)) return true;
    return priceOf(product) === null;
  }

  function isApparel(product) {
    return hasTag(product, 'apparel');
  }

  function inBudget(price, budgetKey) {
    var band = BUDGETS[budgetKey] || BUDGETS.none;
    if (price === null || price === undefined) return false;
    if (band.minExclusive ? price <= band.min : price < band.min) return false;
    if (band.maxExclusive ? price >= band.max : price > band.max) return false;
    return true;
  }

  /* How far a price sits outside a band, 0 when inside. Used to find the closest pieces. */
  function distanceFromBudget(price, budgetKey) {
    var band = BUDGETS[budgetKey] || BUDGETS.none;
    if (inBudget(price, budgetKey)) return 0;
    if (price < band.min || (band.minExclusive && price <= band.min)) return band.min - price + (band.minExclusive ? 1 : 0);
    return price - band.max + (band.maxExclusive ? 1 : 0);
  }

  /*
    Takes pieces from an ordered list until there are `count`, keeping variety: no more
    than `perType` of one product type on the first pass, then tops up in order if still
    short. `start` holds pieces already chosen; they count towards both limits.
  */
  function takeVaried(list, count, perType, start) {
    var taken = (start || []).slice();
    var seen = {};
    /* Apparel counts as one type, so dresses, shirts and kurtas together never crowd out the home pieces. */
    var typeOf = function (product) { return isApparel(product) ? 'apparel' : String(product.product_type || '').toLowerCase(); };
    taken.forEach(function (product) { seen[typeOf(product)] = (seen[typeOf(product)] || 0) + 1; });
    list.forEach(function (product) {
      if (taken.length >= count || taken.indexOf(product) !== -1) return;
      var type = typeOf(product);
      if ((seen[type] || 0) >= perType) return;
      seen[type] = (seen[type] || 0) + 1;
      taken.push(product);
    });
    list.forEach(function (product) {
      if (taken.length >= count) return;
      if (taken.indexOf(product) === -1) taken.push(product);
    });
    return taken;
  }

  function unique(products) {
    var seen = {};
    return (products || []).filter(function (product) {
      if (!product || seen[product.handle]) return false;
      seen[product.handle] = true;
      return true;
    });
  }

  /*
    Self result: `count` pieces in the collection's own order, available ones first,
    sold out pieces only when there is nothing else to show.
  */
  function pickForMood(products, count) {
    var pool = unique(products).filter(function (p) { return !isExcluded(p); });
    var available = pool.filter(isAvailable);
    var soldOut = pool.filter(function (p) { return !isAvailable(p); });
    var picks = takeVaried(available, count, 2);
    if (picks.length < count) picks = picks.concat(soldOut.slice(0, count - picks.length));
    return picks;
  }

  /*
    Gift result: `count` available pieces inside the budget, no apparel, pieces tagged
    Gift first, one per product type where possible. When the mood has fewer than
    `count` in the budget, the closest priced pieces fill the gap and relaxed is true
    so the page can say so.
  */
  function pickGifts(products, budgetKey, count) {
    var pool = unique(products).filter(function (p) {
      return !isExcluded(p) && !isApparel(p) && isAvailable(p);
    });
    var inside = pool.filter(function (p) { return inBudget(priceOf(p), budgetKey); });
    var tagged = inside.filter(function (p) { return hasTag(p, 'gift'); });
    var others = inside.filter(function (p) { return !hasTag(p, 'gift'); });
    /* Every Gift tagged piece in the budget is used before any untagged one. */
    var picks = takeVaried(others, count, 1, takeVaried(tagged, count, 1));
    var relaxed = false;
    if (picks.length < count) {
      var outside = pool
        .filter(function (p) { return !inBudget(priceOf(p), budgetKey); })
        .map(function (p, index) { return { p: p, d: distanceFromBudget(priceOf(p), budgetKey), i: index }; })
        .sort(function (a, b) { return a.d - b.d || a.i - b.i; })
        .map(function (entry) { return entry.p; });
      var extra = outside.slice(0, count - picks.length);
      if (extra.length) relaxed = true;
      picks = picks.concat(extra);
    }
    return { picks: picks, relaxed: relaxed, matched: inside.length };
  }

  /* "Rs 1,499": Indian digit grouping, no decimals. */
  function formatRs(value) {
    var n = Math.round(Number(value) || 0);
    var digits = String(Math.abs(n));
    var head = digits.length > 3 ? digits.slice(0, -3) : '';
    var tail = digits.slice(-3);
    if (head) head = head.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',';
    return (n < 0 ? '-' : '') + '₹' + head + tail;
  }

  /*
    Price in the store's money format (shop.money_format, today "₹{{amount_no_decimals}}"),
    so the cards read like every other price on the site: 1499 -> "₹1,499". value is in
    rupees, as products.json gives it. A no_decimals format rounds to whole rupees; other
    formats keep paise only when there are some. Without a usable format, formatRs is used.
  */
  function formatPrice(value, format) {
    var pattern = String(format || '').replace(/<[^>]*>/g, '');
    var token = /\{\{\s*(\w+)\s*\}\}/.exec(pattern);
    if (!token) return formatRs(value);
    var n = Number(value);
    if (isNaN(n)) n = 0;
    var noDecimals = /no_decimals/.test(token[1]);
    var paise = Math.round(n * 100);
    var whole = noDecimals || paise % 100 === 0;
    var amount = (noDecimals ? Math.round(n) : paise / 100).toLocaleString('en-IN', {
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2
    });
    return pattern.replace(token[0], amount);
  }

  /* Collection link narrowed to a budget with Shopify's own price filter (values in rupees). */
  function budgetUrl(collectionUrl, budgetKey) {
    var band = BUDGETS[budgetKey];
    if (!band || budgetKey === 'none') return collectionUrl;
    var params = [];
    if (band.min > 0) params.push('filter.v.price.gte=' + (band.minExclusive ? band.min + 1 : band.min));
    if (band.max !== Infinity) params.push('filter.v.price.lte=' + (band.maxExclusive ? band.max - 1 : band.max));
    if (!params.length) return collectionUrl;
    return collectionUrl + (collectionUrl.indexOf('?') === -1 ? '?' : '&') + params.join('&');
  }

  /* Shopify CDN image at a given width. */
  function imageUrl(src, width) {
    if (!src) return '';
    var clean = String(src).replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '');
    return clean + (clean.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /* "You are mostly {mood}" with {mood} and friends swapped in. */
  function fill(template, values) {
    return String(template || '').replace(/\{(\w+)\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  /* A stored mood is only trusted when it names one of the four moods. */
  function readStoredMood(raw) {
    try {
      var saved = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!saved || !isMood(saved.mood)) return null;
      return { mood: saved.mood, second: isMood(saved.second) ? saved.second : null, at: Number(saved.at) || 0 };
    } catch (e) {
      return null;
    }
  }

  var logic = {
    MOODS: MOODS,
    BUDGETS: BUDGETS,
    isMood: isMood,
    scoreAnswers: scoreAnswers,
    hasTag: hasTag,
    firstAvailableVariant: firstAvailableVariant,
    isAvailable: isAvailable,
    priceOf: priceOf,
    compareAtOf: compareAtOf,
    isExcluded: isExcluded,
    isApparel: isApparel,
    inBudget: inBudget,
    distanceFromBudget: distanceFromBudget,
    pickForMood: pickForMood,
    pickGifts: pickGifts,
    formatRs: formatRs,
    formatPrice: formatPrice,
    budgetUrl: budgetUrl,
    imageUrl: imageUrl,
    fill: fill,
    readStoredMood: readStoredMood
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = logic;
  if (!root || !root.document) return;
  if (root.mmMoodQuizReady) return;
  root.mmMoodQuizReady = true;
  root.mmMoodLogic = logic;

  /* ---------- 2. The quiz ---------- */

  var doc = root.document;
  var STORE_KEY = 'mmMood';

  /*
    English fallback for the words kept in the locale file (mm.quiz), used when a key is
    missing or empty. {mood} and {count} are filled in by fill(); {title} is the piece.
  */
  var DEFAULT_TEXT = {
    progress: 'Question {current} of {total}',
    collectionButton: 'See all of {mood}',
    moreBudget: 'See more {mood} in this budget',
    moreAny: 'See all of {mood}',
    relaxed: 'Few {mood} pieces sit in that budget, so we have added the closest ones.',
    loading: 'Finding pieces for this mood',
    loadedOne: '1 piece from {mood}',
    loadedOther: '{count} pieces from {mood}',
    empty: 'Nothing to show just now. The full {mood} collection is one tap away.',
    error: 'We could not load the pieces. The full {mood} collection is one tap away.',
    soldOut: 'Sold out',
    addToTote: 'Add to tote',
    addToToteLabel: 'Add to tote: {title}',
    adding: 'Adding',
    added: 'Added to your tote.',
    addError: 'We could not add that to your tote. Please try again.'
  };

  /* The t filter HTML-escapes, so locale words are decoded once before use as textContent. */
  function mergeLocale(text, given) {
    var decoder = doc.createElement('textarea');
    Object.keys(DEFAULT_TEXT).forEach(function (key) {
      var value = given && given[key];
      if (typeof value === 'string' && value.trim() && !/translation missing/i.test(value)) {
        decoder.innerHTML = value;
        text[key] = decoder.value;
      } else {
        text[key] = DEFAULT_TEXT[key];
      }
    });
    return text;
  }

  /* Quiz budget keys mapped to the reminder panel's budget values (snippets/mm-remind.liquid). */
  var REMIND_BUDGET = { 'under-1000': 'under-1000', '1000-2500': '1000-2500' };

  function reducedMotion() {
    return !!(root.matchMedia && root.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function el(tag, className, text) {
    var node = doc.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  function getStoredMood() {
    try { return readStoredMood(root.localStorage.getItem(STORE_KEY)); } catch (e) { return null; }
  }

  /* One fetch per collection per page view, shared by every quiz on the page. */
  var productCache = {};
  function fetchProducts(collectionsUrl, handle) {
    var url = collectionsUrl.replace(/\/$/, '') + '/' + encodeURIComponent(handle) + '/products.json?limit=250';
    if (!productCache[url]) {
      productCache[url] = root.fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (r) {
          if (!r.ok) throw new Error('products ' + r.status);
          return r.json();
        })
        .then(function (json) { return (json && json.products) || []; })
        .catch(function (err) {
          delete productCache[url];
          throw err;
        });
    }
    return productCache[url];
  }

  function Quiz(rootEl) {
    this.root = rootEl;
    var configEl = rootEl.querySelector('[data-mm-quiz-config]');
    try { this.config = JSON.parse(configEl.textContent); } catch (e) { this.config = null; }
    if (!this.config) return;
    this.config.text = mergeLocale(this.config.text || {}, this.config.i18n);

    this.mode = this.config.mode === 'gift' ? 'gift' : 'self';
    this.steps = Array.prototype.slice.call(rootEl.querySelectorAll('[data-mm-quiz-step]'));
    this.questionCount = this.steps.filter(function (s) { return s.dataset.mmQuizStep === 'question'; }).length;
    this.answers = [];
    this.budget = null;
    this.who = null;
    this.index = 0;
    this.requestId = 0;

    this.bar = rootEl.querySelector('[data-mm-quiz-bar]');
    this.backBtn = rootEl.querySelector('[data-mm-quiz-back]');
    this.progressText = rootEl.querySelector('[data-mm-quiz-progress-text]');
    this.progressBar = rootEl.querySelector('[data-mm-quiz-progress]');
    this.progressFill = rootEl.querySelector('[data-mm-quiz-progress-fill]');
    this.head = rootEl.querySelector('[data-mm-quiz-head]');
    this.result = rootEl.querySelector('[data-mm-quiz-result]');
    this.status = rootEl.querySelector('[data-mm-quiz-status]');
    this.grid = rootEl.querySelector('[data-mm-quiz-grid]');

    if (!this.steps.length || !this.result) return;
    rootEl.classList.add('mm-quiz--js');
    rootEl.addEventListener('click', this.onClick.bind(this));

    if (!this.restore()) this.show(0, false);
  }

  Quiz.prototype.stateKey = function () {
    return 'mmQuizState:' + this.mode;
  };

  /* The finished result survives a reload in the same tab (the welcome form posts the page). */
  Quiz.prototype.save = function () {
    try {
      root.sessionStorage.setItem(this.stateKey(), JSON.stringify({ answers: this.answers, budget: this.budget, who: this.who }));
    } catch (e) {}
  };

  Quiz.prototype.forget = function () {
    try { root.sessionStorage.removeItem(this.stateKey()); } catch (e) {}
  };

  Quiz.prototype.restore = function () {
    if (this.config.designMode) return false;
    var saved = null;
    try { saved = JSON.parse(root.sessionStorage.getItem(this.stateKey()) || 'null'); } catch (e) {}
    if (!saved || !Array.isArray(saved.answers) || saved.answers.length !== this.questionCount) return false;
    if (!saved.answers.every(isMood)) return false;
    if (this.mode === 'gift' && !BUDGETS[saved.budget]) return false;
    this.answers = saved.answers;
    this.budget = saved.budget || null;
    this.who = saved.who || null;
    this.finish(true);
    return true;
  };

  Quiz.prototype.onClick = function (event) {
    var target = event.target;
    var answer = target.closest('[data-mm-quiz-answer]');
    if (answer) { this.choose(answer); return; }
    var who = target.closest('[data-mm-quiz-who]');
    if (who) { this.chooseWho(who); return; }
    var budget = target.closest('[data-mm-quiz-budget]');
    if (budget) { this.chooseBudget(budget); return; }
    if (target.closest('[data-mm-quiz-back]')) { this.back(); return; }
    if (target.closest('[data-mm-quiz-restart]')) { this.restart(); return; }
    var add = target.closest('[data-mm-quiz-add]');
    if (add) { this.addToTote(add); return; }
    if (target.closest('[data-mm-quiz-email]')) { this.openWelcome(); }
  };

  Quiz.prototype.track = function (name, data) {
    try {
      if (root.Shopify && root.Shopify.analytics && typeof root.Shopify.analytics.publish === 'function') {
        root.Shopify.analytics.publish(name, data);
      }
    } catch (e) {}
  };

  Quiz.prototype.choose = function (button) {
    var mood = button.dataset.mmQuizAnswer;
    if (!isMood(mood)) return;
    if (this.index === 0 && !this.started) { this.started = true; this.track('mm_quiz_start', { mode: this.mode }); }
    this.answers[this.index] = mood;
    this.answers.length = this.index + 1;
    this.markPressed(this.steps[this.index], '[data-mm-quiz-answer]', button);
    if (this.index + 1 < this.steps.length) this.show(this.index + 1, true);
    else this.finish(false);
  };

  Quiz.prototype.chooseWho = function (button) {
    var already = button.getAttribute('aria-pressed') === 'true';
    this.markPressed(this.steps[this.index], '[data-mm-quiz-who]', already ? null : button);
    this.who = already ? null : button.dataset.mmQuizWho;
  };

  Quiz.prototype.chooseBudget = function (button) {
    if (!BUDGETS[button.dataset.mmQuizBudget]) return;
    this.budget = button.dataset.mmQuizBudget;
    this.markPressed(this.steps[this.index], '[data-mm-quiz-budget]', button);
    this.finish(false);
  };

  Quiz.prototype.markPressed = function (scope, selector, pressed) {
    Array.prototype.forEach.call(scope.querySelectorAll(selector), function (b) {
      b.setAttribute('aria-pressed', b === pressed ? 'true' : 'false');
    });
  };

  Quiz.prototype.back = function () {
    if (!this.result.hidden) { this.show(this.steps.length - 1, true); return; }
    if (this.index > 0) this.show(this.index - 1, true);
  };

  Quiz.prototype.restart = function () {
    var self = this;
    this.answers = [];
    this.budget = null;
    this.who = null;
    this.requestId += 1;
    this.started = false;
    this.forget();
    this.steps.forEach(function (step) {
      self.markPressed(step, '[aria-pressed]', null);
    });
    this.show(0, true);
  };

  /* Shows one step. `move` is false on first paint so the page does not jump or steal focus. */
  Quiz.prototype.show = function (index, move) {
    this.index = index;
    this.result.hidden = true;
    this.root.classList.remove('mm-quiz--done');
    if (this.bar) this.bar.hidden = false;
    this.steps.forEach(function (step, i) {
      step.hidden = i !== index;
      step.classList.toggle('is-active', i === index);
    });
    if (this.backBtn) this.backBtn.hidden = index === 0;

    var total = this.steps.length;
    if (this.progressText) {
      this.progressText.textContent = fill(this.config.text.progress, { current: index + 1, total: total });
    }
    if (this.progressBar) {
      this.progressBar.setAttribute('aria-valuenow', String(index + 1));
      this.progressBar.setAttribute('aria-valuetext', fill(this.config.text.progress, { current: index + 1, total: total }));
    }
    if (this.progressFill) this.progressFill.style.width = Math.round(((index + 1) / total) * 100) + '%';

    if (move) this.focusOn(this.steps[index].querySelector('[data-mm-quiz-heading]'));
  };

  Quiz.prototype.focusOn = function (node) {
    if (!node) return;
    var top = this.root.getBoundingClientRect().top;
    if (top < 0) this.root.scrollIntoView({ block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth' });
    node.focus({ preventScroll: true });
  };

  Quiz.prototype.moodName = function (mood) {
    var entry = this.config.moods[mood];
    return (entry && entry.name) || (mood.charAt(0).toUpperCase() + mood.slice(1));
  };

  Quiz.prototype.collectionUrl = function (mood) {
    var entry = this.config.moods[mood] || {};
    return this.config.collectionsUrl.replace(/\/$/, '') + '/' + (entry.handle || mood);
  };

  Quiz.prototype.finish = function (restored) {
    var outcome = scoreAnswers(this.answers);
    if (!outcome.mood) { this.show(0, !restored); return; }
    var self = this;
    var text = this.config.text;
    var mood = outcome.mood;
    var names = { mood: this.moodName(mood), second: outcome.second ? this.moodName(outcome.second) : '' };

    this.steps.forEach(function (step) { step.hidden = true; step.classList.remove('is-active'); });
    if (this.bar) this.bar.hidden = true;
    this.result.hidden = false;
    this.root.classList.add('mm-quiz--done');
    this.result.style.setProperty('--mm-mood', 'var(--mm-mood-' + mood + ')');

    var heading = this.result.querySelector('[data-mm-quiz-result-heading]');
    heading.textContent = fill(outcome.second ? text.resultPair : text.resultSingle, names);
    this.result.querySelector('[data-mm-quiz-result-text]').textContent = (this.config.moods[mood] || {}).text || '';

    var collectionLink = this.result.querySelector('[data-mm-quiz-collection]');
    if (collectionLink) {
      collectionLink.href = this.collectionUrl(mood);
      collectionLink.querySelector('[data-mm-quiz-collection-label]').textContent = fill(text.collectionButton, names);
    }
    var picksHeading = this.result.querySelector('[data-mm-quiz-picks-heading]');
    if (picksHeading) {
      var whoLabel = this.who && this.config.who[this.who] ? this.config.who[this.who].toLowerCase() : text.whoFallback;
      picksHeading.textContent = fill(text.picksHeading, { who: whoLabel, mood: names.mood });
    }
    var moreLink = this.result.querySelector('[data-mm-quiz-more]');
    if (moreLink) {
      moreLink.href = budgetUrl(this.collectionUrl(mood), this.budget);
      moreLink.textContent = fill(this.budget === 'none' ? text.moreAny : text.moreBudget, names);
    }

    var emailLine = this.result.querySelector('[data-mm-quiz-email-line]');
    if (emailLine) emailLine.hidden = !this.welcomeAvailable();

    /* Gift finder: the date reminder starts with their mood and, where the bands match, the budget. */
    var remind = this.result.querySelector('[data-mm-quiz-remind]');
    if (remind) {
      var remindMood = remind.querySelector('select[name="mood"]');
      if (remindMood) remindMood.value = mood;
      var remindBudget = remind.querySelector('select[name="budget"]');
      if (remindBudget) remindBudget.value = REMIND_BUDGET[this.budget] || '';
    }

    if (!restored) {
      this.track('mm_quiz_complete', { mode: this.mode, mood: mood, second: outcome.second || '', budget: this.budget || '', who: this.who || '' });
      this.save();
      if (this.mode === 'self') this.remember(outcome);
      this.focusOn(heading);
    }
    this.loadProducts(mood, names);
  };

  /* Self mode only: remember the mood and tell the rest of the page. */
  Quiz.prototype.remember = function (outcome) {
    var saved = { mood: outcome.mood, second: outcome.second, at: Date.now() };
    try { root.localStorage.setItem(STORE_KEY, JSON.stringify(saved)); } catch (e) {}
    doc.dispatchEvent(new CustomEvent('mm:mood', {
      detail: { mood: saved.mood, second: saved.second, at: saved.at, source: 'quiz' }
    }));
  };

  Quiz.prototype.loadProducts = function (mood, names) {
    var self = this;
    var text = this.config.text;
    var request = ++this.requestId;
    var note = this.result.querySelector('[data-mm-quiz-note]');
    this.grid.textContent = '';
    this.grid.setAttribute('aria-busy', 'true');
    if (note) note.hidden = true;
    this.status.textContent = text.loading;

    fetchProducts(this.config.collectionsUrl, (this.config.moods[mood] || {}).handle || mood)
      .then(function (products) {
        if (request !== self.requestId) return;
        var picks;
        var relaxed = false;
        if (self.mode === 'gift') {
          var chosen = pickGifts(products, self.budget, self.config.giftCount);
          picks = chosen.picks;
          relaxed = chosen.relaxed;
        } else {
          picks = pickForMood(products, self.config.selfCount);
        }
        self.grid.removeAttribute('aria-busy');
        if (!picks.length) { self.status.textContent = fill(text.empty, names); return; }
        picks.forEach(function (product) { self.grid.appendChild(self.card(product)); });
        self.status.textContent = fill(picks.length === 1 ? text.loadedOne : text.loadedOther, { count: picks.length, mood: names.mood });
        if (note && relaxed) {
          note.textContent = fill(text.relaxed, names);
          note.hidden = false;
        }
      })
      .catch(function (err) {
        if (request !== self.requestId) return;
        console.error('[mm-mood-quiz] products failed', err);
        self.grid.removeAttribute('aria-busy');
        self.status.textContent = fill(text.error, names);
      });
  };

  /* A card built to sit beside the theme's own product cards: 4:5 image, name and price on one row, type below. */
  Quiz.prototype.card = function (product) {
    var text = this.config.text;
    var url = (this.config.rootUrl || '/').replace(/\/$/, '') + '/products/' + product.handle;
    var item = el('li', 'mm-quiz-card');
    var media = el('a', 'mm-quiz-card__media');
    media.href = url;
    media.setAttribute('aria-hidden', 'true');
    media.tabIndex = -1;
    var image = (product.images || [])[0];
    if (image && image.src) {
      var img = doc.createElement('img');
      img.className = 'mm-quiz-card__img';
      img.src = imageUrl(image.src, 533);
      img.srcset = [360, 533, 720].map(function (w) { return imageUrl(image.src, w) + ' ' + w + 'w'; }).join(', ');
      img.sizes = this.mode === 'gift' ? '(min-width: 750px) 300px, 110px' : '(min-width: 990px) 300px, (min-width: 750px) 33vw, 50vw';
      img.alt = '';
      img.loading = 'lazy';
      if (image.width && image.height) { img.width = image.width; img.height = image.height; }
      media.appendChild(img);
    }
    if (!isAvailable(product)) media.appendChild(el('span', 'mm-quiz-card__tag', text.soldOut));
    item.appendChild(media);

    var info = el('div', 'mm-quiz-card__info');
    var row = el('div', 'mm-quiz-card__row');
    var title = el('h3', 'mm-quiz-card__title');
    var link = el('a', null, product.title);
    link.href = url;
    title.appendChild(link);
    row.appendChild(title);
    var price = el('div', 'mm-quiz-card__price');
    price.appendChild(el('span', 'mm-quiz-card__price-current', formatPrice(priceOf(product), this.config.moneyFormat)));
    var compare = compareAtOf(product);
    if (compare) {
      var was = el('s', 'mm-quiz-card__price-compare', formatPrice(compare, this.config.moneyFormat));
      price.appendChild(was);
    }
    row.appendChild(price);
    info.appendChild(row);
    if (product.product_type) info.appendChild(el('div', 'mm-quiz-card__cat', product.product_type));

    if (this.mode === 'gift') {
      var variant = firstAvailableVariant(product);
      if (variant) {
        var add = el('button', 'mm-btn mm-btn--solid mm-btn--sm mm-quiz-card__add', text.addToTote);
        add.type = 'button';
        add.dataset.mmQuizAdd = String(variant.id);
        add.dataset.productId = String(product.id);
        add.setAttribute('aria-label', fill(text.addToToteLabel, { title: product.title }));
        info.appendChild(add);
        var msg = el('p', 'mm-quiz-card__msg');
        msg.setAttribute('role', 'status');
        msg.setAttribute('data-mm-quiz-add-msg', '');
        info.appendChild(msg);
      }
    }
    item.appendChild(info);
    return item;
  };

  /* Same route as assets/mm-cart-pairs.js: add, then let the theme's drawer render itself. */
  Quiz.prototype.addToTote = function (button) {
    if (button.disabled) return;
    var text = this.config.text;
    var cart = doc.querySelector('cart-drawer');
    if (!cart || typeof cart.renderContents !== 'function') cart = null;
    var body = {
      items: [{ id: Number(button.dataset.mmQuizAdd), quantity: 1, properties: { _mm_source: 'gift-finder' } }]
    };
    if (cart) {
      body.sections = cart.getSectionsToRender().map(function (s) { return s.id; });
      body.sections_url = root.location.pathname;
    }
    var label = button.textContent;
    var msg = button.parentNode.querySelector('[data-mm-quiz-add-msg]');
    var reset = function () {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.textContent = label;
    };
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = text.adding;
    if (msg) msg.textContent = '';

    root.fetch((root.routes && root.routes.cart_add_url ? root.routes.cart_add_url : '/cart/add') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json().then(function (json) { return { ok: r.ok, json: json }; }); })
      .then(function (res) {
        if (!res.ok) throw new Error(res.json.description || res.json.message || 'add failed');
        reset();
        if (msg) msg.textContent = text.added;
        if (cart) {
          var first = res.json.items && res.json.items[0];
          /* An empty drawer hides its items until this class goes, as the theme's own add button does. */
          cart.classList.remove('is-empty');
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(button);
          cart.renderContents({ id: first ? first.product_id : null, sections: res.json.sections });
        } else {
          root.location.href = (root.routes && root.routes.cart_url) || '/cart';
        }
        if (typeof root.publish === 'function' && root.PUB_SUB_EVENTS) {
          root.publish(root.PUB_SUB_EVENTS.cartUpdate, { source: 'mm-mood-quiz' });
        }
      })
      .catch(function (err) {
        reset();
        var shown = err && err.message && err.message !== 'add failed' ? err.message : text.addError;
        if (msg) msg.textContent = shown;
      });
  };

  /* The "Email me this edit" line only appears when the popup is on the page and the code is not already claimed. */
  Quiz.prototype.welcomeAvailable = function () {
    if (!doc.querySelector('mm-welcome-popup')) return false;
    try {
      var saved = JSON.parse(root.localStorage.getItem('mmWelcome') || 'null');
      if (saved && saved.state === 'claimed') return false;
    } catch (e) {}
    return true;
  };

  Quiz.prototype.openWelcome = function () {
    if (!doc.querySelector('mm-welcome-popup')) return;
    var outcome = scoreAnswers(this.answers);
    doc.dispatchEvent(new CustomEvent('mm:welcome-open', {
      detail: { source: this.mode === 'gift' ? 'gift-finder' : 'mood-quiz', mood: outcome.mood }
    }));
  };

  /* ---------- 3. The mood hint ---------- */

  /* Words come from the hint's own data-mm-i18n attribute (locale keys mm.mood_hint), read
     each time it is filled because the tote drawer brings a fresh copy on every re-render.
     "[mood]" marks where the linked mood name goes. English is the fallback. */
  function hintWords(hint) {
    var words = {};
    try { words = JSON.parse(hint.getAttribute('data-mm-i18n') || '{}') || {}; } catch (e) { words = {}; }
    var usable = function (text) {
      return typeof text === 'string' && text !== '' && text.indexOf('translation missing') === -1;
    };
    var decode = function (text) {
      if (text.indexOf('&') === -1) return text;
      var box = doc.createElement('textarea');
      box.innerHTML = text;
      return box.value;
    };
    var picked = usable(words.picked) && words.picked.indexOf('[mood]') !== -1 ? words.picked : 'Picked for your mood: [mood]';
    var names = words.moods || {};
    return {
      picked: decode(picked),
      name: function (mood) {
        return usable(names[mood]) ? decode(names[mood]) : mood.charAt(0).toUpperCase() + mood.slice(1);
      }
    };
  }

  function fillHints() {
    var saved = getStoredMood();
    Array.prototype.forEach.call(doc.querySelectorAll('[data-mm-mood-hint]'), function (hint) {
      if (!saved) return;
      if (hint.dataset.mmMoodHintFilled === saved.mood) return;
      var base = (hint.dataset.collectionsUrl || '/collections').replace(/\/$/, '');
      var words = hintWords(hint);
      var link = el('a', 'mm-mood-hint__link', words.name(saved.mood));
      link.href = base + '/' + saved.mood;
      var at = words.picked.indexOf('[mood]');
      var before = words.picked.slice(0, at);
      var after = words.picked.slice(at + '[mood]'.length);
      hint.textContent = '';
      if (before) hint.appendChild(el('span', 'mm-mood-hint__text', before));
      hint.appendChild(link);
      if (after) hint.appendChild(el('span', 'mm-mood-hint__text', after));
      hint.style.setProperty('--mm-mood', 'var(--mm-mood-' + saved.mood + ')');
      hint.dataset.mmMoodHintFilled = saved.mood;
    });
  }

  function watchDrawer() {
    /* The tote drawer swaps its contents after every change, which brings back the plain hint. */
    var drawer = doc.querySelector('cart-drawer');
    if (!drawer || !root.MutationObserver) return;
    var queued = false;
    new root.MutationObserver(function () {
      if (queued) return;
      queued = true;
      root.setTimeout(function () { queued = false; fillHints(); }, 50);
    }).observe(drawer, { childList: true, subtree: true });
  }

  function initQuizzes(scope) {
    Array.prototype.forEach.call((scope || doc).querySelectorAll('[data-mm-quiz]'), function (node) {
      if (node.mmQuiz) return;
      node.mmQuiz = new Quiz(node);
    });
  }

  function start() {
    initQuizzes(doc);
    fillHints();
    watchDrawer();
  }

  doc.addEventListener('mm:mood', fillHints);

  /* Theme editor: rebuild after a section reload, and jump to the question being edited. */
  doc.addEventListener('shopify:section:load', function (event) { initQuizzes(event.target); fillHints(); });
  doc.addEventListener('shopify:block:select', function (event) {
    var step = event.target.closest && event.target.closest('[data-mm-quiz-step]');
    var quizEl = step && step.closest('[data-mm-quiz]');
    if (!quizEl || !quizEl.mmQuiz || !quizEl.mmQuiz.steps) return;
    var index = quizEl.mmQuiz.steps.indexOf(step);
    if (index !== -1) quizEl.mmQuiz.show(index, false);
  });

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start);
  else start();
})(typeof window !== 'undefined' ? window : null);
